import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@/models';
import { login, superadminLogin, register, logout as apiLogout, getCurrentUser, isAuthenticated } from '@/services/api/auth';
import { useTranslation } from './useTranslation';
import { toast } from 'sonner';
import { USER_ROUTES, PUBLIC_ROUTES, ADMIN_ROUTES } from '@/routes';
import { pendingDeletionMessage } from '@/utils/registration-error';

type LoginOptions = {
  redirectTo?: string;
  suppressToast?: boolean;
};

type RegisterOptions = {
  skipRedirect?: boolean;
  website?: string;
};

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const locale = useRouteLocale();
  const { t, currentLanguage } = useTranslation();
  const currentLocale = locale || 'ro';

  // Initialize auth state from sessionStorage
  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const extractErrorMessage = useCallback((error: unknown, defaultKey: string): string => {
    const errorObj = error as { response?: { data?: { error?: { message_key?: string; code?: string; message?: string; errors?: Record<string, string[]> }; message?: string } }; message?: string };
    const errorData = errorObj?.response?.data;
    let errorMessage = t(defaultKey);

    if (errorData) {
      if (errorData.error) {
        const errorKey = errorData.error.message_key || errorData.error.code || defaultKey;
        const translatedError = t(`errors.${errorKey}`);

        if (translatedError && translatedError !== `errors.${errorKey}`) {
          errorMessage = translatedError;
        } else if (errorData.error.message) {
          errorMessage = errorData.error.message;
        }

        // Handle validation errors
        if (errorData.error.errors && Object.keys(errorData.error.errors).length > 0) {
          const firstError = Object.values(errorData.error.errors)[0] as string[];
          if (firstError && firstError.length > 0) {
            errorMessage = firstError[0];
          }
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (errorObj?.message) {
      errorMessage = errorObj.message;
    }

    return errorMessage;
  }, [t]);

  const handleLogin = useCallback(async (email: string, password: string, options?: LoginOptions) => {
    try {
      setLoading(true);
      const response = await login({ user_email: email, user_password: password });
      setUser(response.user);
      if (!options?.suppressToast) {
        toast.success(t('auth.loginSuccess'));
      }
      
      // Redirect administrators to management page, regular users to events
      const redirectTo = options?.redirectTo ?? 
        (response.user?.user_admin 
          ? ADMIN_ROUTES.management(currentLocale) 
          : USER_ROUTES.events(currentLocale));
      
      // Defer navigation to next event loop tick to allow React to complete
      // the current render cycle before unmounting. This prevents premature
      // component unmounting that can cause issues in E2E tests and ensures
      // a smoother user experience.
      setTimeout(() => {
        navigate(redirectTo);
      }, 0);
      
      return response;
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error, 'auth.invalidCredentials');
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [navigate, currentLocale, t, extractErrorMessage]);

  const handleSuperadminLogin = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await superadminLogin({ user_email: email, user_password: password });
      setUser(response.user);
      toast.success(t('auth.loginSuccess'));
      setTimeout(() => {
        navigate(ADMIN_ROUTES.management(currentLocale));
      }, 0);
      return response;
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error, 'auth.invalidCredentials');
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [navigate, currentLocale, t, extractErrorMessage]);

  const handleRegister = useCallback(async (email: string, password: string, name: string, phone?: string, options?: RegisterOptions) => {
    try {
      setLoading(true);
      const response = await register({
        user_email: email,
        user_password: password,
        user_name: name,
        user_phone: phone,
        ...(options?.website ? { website: options.website } : {}),
      });
      
      toast.success(t('auth.registrationSuccess'));
      
      if (!options?.skipRedirect) {
        navigate(PUBLIC_ROUTES.emailVerification(currentLocale));
      }
      return response;
    } catch (error: unknown) {
      const retentionMessage = pendingDeletionMessage(error, t, currentLanguage);
      const errorMessage = retentionMessage ?? extractErrorMessage(error, 'common.error');
      toast.error(errorMessage, retentionMessage ? { duration: 10000 } : undefined);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [navigate, currentLocale, currentLanguage, t, extractErrorMessage]);

  const handleLogout = useCallback(async () => {
    try {
      setLoading(true);
      await apiLogout();
      setUser(null);
      toast.success(t('auth.logoutSuccess'));
      
      // Redirect to landing page
      navigate(PUBLIC_ROUTES.home(currentLocale));
    } catch {
      // Even if logout fails, clear user state
      setUser(null);
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, [navigate, currentLocale, t]);

  const updateUserState = useCallback((updater: (prev: User | null) => User | null) => {
    setUser((previous) => {
      const next = updater(previous);
      if (typeof window !== 'undefined') {
        if (next) {
          sessionStorage.setItem('user', JSON.stringify(next));
        } else {
          sessionStorage.removeItem('user');
        }
      }
      return next;
    });
  }, []);

  return {
    user,
    loading,
    isAuthenticated: isAuthenticated(),
    login: handleLogin,
    loginSuperadmin: handleSuperadminLogin,
    register: handleRegister,
    logout: handleLogout,
    updateUser: updateUserState,
  };
}
